import { useCallback, useState } from "react";
import { toast } from "sonner";
import { verify_connection_bizminer_admin_organization_sso_config_path } from "@/routes";
import type { BizminerAdminSsoConfig } from "@/types/serializers";

export type TestStatus = "idle" | "testing" | "success" | "error";

interface TestResultMessage {
  type: "sso_test_result";
  success: boolean;
  provider?: string;
  email?: string;
  name?: string;
  error?: string;
}

interface UseSsoTestConnectionOptions {
  organizationId: number;
  ssoConfig: BizminerAdminSsoConfig;
}

export function useSsoTestConnection({
  organizationId,
  ssoConfig,
}: UseSsoTestConnectionOptions) {
  const [testStatus, setTestStatus] = useState<TestStatus>("idle");

  const handleTestConnection = useCallback(() => {
    if (!ssoConfig.sso_enabled || !ssoConfig.sso_provider) {
      toast.error("SSO must be enabled and configured to test");
      return;
    }

    setTestStatus("testing");

    // We need to go through the server to set session flags for test mode
    // The server page will auto-submit a POST form to the OAuth endpoint
    const verifyUrl =
      verify_connection_bizminer_admin_organization_sso_config_path(
        organizationId,
      );
    const popup = window.open(
      verifyUrl,
      "sso_verify",
      "width=600,height=700,popup=yes",
    );

    if (!popup) {
      setTestStatus("error");
      toast.error("Please allow popups to test SSO connection");
      return;
    }

    const handleMessage = (event: MessageEvent<TestResultMessage>) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "sso_test_result") return;

      window.removeEventListener("message", handleMessage);

      if (event.data.success) {
        setTestStatus("success");
        toast.success(
          `Connection successful! Authenticated as ${event.data.email}`,
        );
      } else {
        setTestStatus("error");
        toast.error(`Connection failed: ${event.data.error}`);
      }

      setTimeout(() => setTestStatus("idle"), 3000);
    };

    window.addEventListener("message", handleMessage);

    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener("message", handleMessage);
        setTestStatus((current) => (current === "testing" ? "idle" : current));
      }
    }, 500);
  }, [organizationId, ssoConfig]);

  return {
    testStatus,
    handleTestConnection,
  };
}
